import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";

export default function Login() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => { if (user) nav("/admin"); }, [user, nav]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await login(email, password);
            nav("/admin");
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-crema">
            {/* Left visual */}
            <div className="hidden lg:flex relative bg-espresso text-white p-12 flex-col justify-between overflow-hidden">
                <div
                    className="absolute inset-0 opacity-[0.07] pointer-events-none"
                    style={{
                        backgroundImage: "url(/bc-bull.png)",
                        backgroundSize: "70%",
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "center 65%",
                    }}
                />
                <div
                    className="absolute -right-32 -bottom-32 w-[520px] h-[520px] rounded-full pointer-events-none"
                    style={{ background: "radial-gradient(circle, rgba(234,119,4,0.25) 0%, rgba(234,119,4,0) 70%)" }}
                />
                <div className="relative">
                    <Logo variant="wordmark-white" size={40} />
                </div>
                <div className="relative">
                    <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[1.05] tracking-tight">
                        Voucher<br/>Redemption<br/>Hub.
                    </h1>
                    <p className="mt-6 text-base text-white/70 max-w-md font-body">
                        Centralised voucher control for every outlet. Scan, validate, redeem.
                    </p>
                </div>
                <div className="relative text-xs text-white/50 tracking-widest uppercase">
                    Voucher Redemption · Master Admin
                </div>
            </div>

            {/* Right form */}
            <div className="flex items-center justify-center p-6 sm:p-12">
                <Card className="w-full max-w-md border-[#E5E0D8] shadow-none rounded-sm">
                    <CardContent className="p-8">
                        <div className="mb-8">
                            <div className="text-xs tracking-widest text-[#7A726D] uppercase mb-2">Sign in</div>
                            <h2 className="font-display text-3xl font-bold text-espresso">Welcome back</h2>
                            <p className="text-sm text-[#7A726D] mt-1">Use your administrator credentials.</p>
                        </div>
                        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
                            <div>
                                <Label htmlFor="email" className="text-xs uppercase tracking-wide text-[#7A726D]">Email</Label>
                                <Input
                                    id="email" type="email" required
                                    value={email} onChange={(e) => setEmail(e.target.value)}
                                    className="mt-1 rounded-sm border-[#E5E0D8] focus-visible:ring-[#C48464]"
                                    data-testid="login-email-input"
                                    autoComplete="email"
                                />
                            </div>
                            <div>
                                <Label htmlFor="password" className="text-xs uppercase tracking-wide text-[#7A726D]">Password</Label>
                                <Input
                                    id="password" type="password" required
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    className="mt-1 rounded-sm border-[#E5E0D8] focus-visible:ring-[#C48464]"
                                    data-testid="login-password-input"
                                    autoComplete="current-password"
                                />
                            </div>
                            {error && (
                                <div data-testid="login-error" className="text-sm text-[#B24A45] border border-[#B24A45]/30 bg-[#B24A45]/5 px-3 py-2 rounded-sm">
                                    {error}
                                </div>
                            )}
                            <Button
                                type="submit" disabled={submitting}
                                data-testid="login-submit-button"
                                className="w-full bg-espresso hover:bg-[#2c1a17] text-white rounded-sm h-11 font-display tracking-wide"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
                            </Button>
                        </form>
                        <div className="mt-6 text-xs text-[#7A726D] text-center">
                            Need to scan a voucher? <a href="/" className="text-bc-orange underline" data-testid="link-scanner">Open scanner →</a>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
